import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, Download, Loader2, Package, Tag, Building2, ArrowLeft, Check } from 'lucide-react';
import { marketplaceApi } from '../api';
import DashboardLayout from '../components/dashboard/DashboardLayout';

export default function MarketplaceDetailPage() {
  const { id } = useParams();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [rating, setRating] = useState(0);
  const [rateMsg, setRateMsg] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await marketplaceApi.get(id);
      setBundle(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Nie znaleziono konfiguracji.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const handleInstall = async () => {
    setInstalling(true);
    try {
      // with_data=true — backend zwraca pełny bundle_data i zlicza instalację
      const r = await marketplaceApi.get(id, true);
      setBundle((b) => ({ ...b, ...r.data }));
      setInstalled(true);
    } catch (e) {
      alert(e.response?.data?.detail || 'Pobranie konfiguracji nie powiodło się.');
    } finally {
      setInstalling(false);
    }
  };

  const handleRate = async (n) => {
    setRating(n);
    try {
      await marketplaceApi.rate(id, n);
      setRateMsg('Dziękujemy za ocenę.');
      load();
    } catch (e) {
      setRateMsg(e.response?.data?.detail || 'Nie udało się zapisać oceny.');
    }
  };

  const avgRating = bundle && bundle.rating_count > 0
    ? (bundle.rating_sum / bundle.rating_count).toFixed(1) : null;

  return (
    <DashboardLayout>
      <Link
        to="/dashboard/marketplace"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Marketplace
      </Link>

      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-slate-500">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>{error}</p>
        </div>
      ) : bundle && (
        <div className="max-w-3xl">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h1 className="text-2xl font-bold text-slate-900">{bundle.title}</h1>
              {bundle.is_verified && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium whitespace-nowrap">
                  Wybór redakcji
                </span>
              )}
            </div>
            <p className="text-slate-600 mb-4">{bundle.description}</p>

            <div className="flex items-center gap-4 text-sm text-slate-500 flex-wrap mb-4">
              <span className="flex items-center gap-1"><Building2 className="w-4 h-4" /> {bundle.source_org_name}</span>
              <span className="flex items-center gap-1"><Package className="w-4 h-4" /> {bundle.category}</span>
              {bundle.industry && <span className="flex items-center gap-1"><Tag className="w-4 h-4" /> {bundle.industry}</span>}
              <span className="flex items-center gap-1"><Download className="w-4 h-4" /> {bundle.installs_count || 0} instalacji</span>
              {avgRating && (
                <span className="flex items-center gap-1 text-amber-600">
                  <Star className="w-4 h-4 fill-current" /> {avgRating} ({bundle.rating_count})
                </span>
              )}
            </div>

            {bundle.tags && bundle.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-5">
                {bundle.tags.map((tag, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">#{tag}</span>
                ))}
              </div>
            )}

            <button
              onClick={handleInstall}
              disabled={installing}
              className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white px-5 py-2.5 rounded-lg text-sm font-medium"
            >
              {installing
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : installed ? <Check className="w-4 h-4" /> : <Download className="w-4 h-4" />}
              {installed ? 'Konfiguracja pobrana' : 'Pobierz konfigurację'}
            </button>
            <p className="text-xs text-slate-400 mt-2">
              Pobranie zwraca pakiet konfiguracji do zaimportowania w Sorto-CRM.
            </p>
          </div>

          {installed && bundle.bundle_data && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 mt-4">
              <h2 className="font-semibold text-slate-900 mb-2">Zawartość pakietu</h2>
              <pre className="text-xs bg-slate-50 border border-slate-100 rounded-lg p-3 overflow-auto max-h-80">
{JSON.stringify(bundle.bundle_data, null, 2)}
              </pre>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 p-6 mt-4">
            <h2 className="font-semibold text-slate-900 mb-3">Oceń konfigurację</h2>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => handleRate(n)} className="p-1" type="button">
                  <Star className={`w-6 h-6 ${n <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
                </button>
              ))}
            </div>
            {rateMsg && <p className="text-sm text-slate-500 mt-2">{rateMsg}</p>}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
